import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Lock, Shield, X } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../../apiWithAccessToken";
import PrincipalSelector from "./PrincipalSelector";
import { InlineDropdown, MaskingBuilder, RlsBuilder } from "./PolicyBuilders";

function defaultState() {
  return {
    id: null,
    name: "",
    scope: "user",
    principalId: null,
    databaseId: "",
    tableName: "",
    rlsEnabled: true,
    maskingEnabled: false,
    rls: { logicalOp: "and", conditions: [] },
    masking: { rules: [] },
  };
}

function createValidationState() {
  return {
    rls: { ok: false, message: "" },
    masking: { ok: false, message: "" },
  };
}

function createPreviewConfig() {
  return {
    rls: { includeSampleRows: true, sampleLimit: 10 },
    masking: { includeSampleRows: true, sampleLimit: 10 },
  };
}

function createPreviewState() {
  return {
    rls: null,
    masking: null,
  };
}

const NULL_OPS = new Set(["is_null", "is_not_null"]);
const LIST_OPS = new Set(["in", "not_in"]);
const POLICY_NAME_MAX_LENGTH = 50;

const toNumberIfNumeric = (value, filterType) => {
  if (filterType !== "numeric") return value;
  const n = Number(value);
  return Number.isNaN(n) ? value : n;
};

const normalizeListValue = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  if (value === undefined || value === null) return [];
  return String(value)
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
};

const normalizeBetweenValue = (value) => {
  if (Array.isArray(value)) return [value[0] ?? "", value[1] ?? ""];
  if (value === undefined || value === null) return ["", ""];
  const parts = String(value)
    .split(",")
    .map((x) => x.trim())
    .slice(0, 2);
  return [parts[0] || "", parts[1] || ""];
};

const rowHasValue = (row) => {
  const op = String(row?.operator || "").trim().toLowerCase();
  if (NULL_OPS.has(op)) return true;
  if (LIST_OPS.has(op)) return normalizeListValue(row?.value).length > 0;
  if (op === "between") {
    const [a, b] = normalizeBetweenValue(row?.value);
    return String(a).trim() !== "" && String(b).trim() !== "";
  }
  return String(row?.value ?? "").trim() !== "";
};

const hasDateBetweenRangeError = (row, columnProfiles) => {
  if (String(row?.operator || "").toLowerCase() !== "between") return false;
  if (columnProfiles[row?.column]?.filter_type !== "date") return false;
  const [fromDate, toDate] = normalizeBetweenValue(row?.value);
  if (!fromDate || !toDate) return false;
  return String(fromDate) > String(toDate);
};

function buildRuleValue(row, columnProfiles) {
  const op = String(row?.operator || "").trim();
  const filterType = columnProfiles[row?.column]?.filter_type;

  if (NULL_OPS.has(op)) return undefined;

  if (op === "between") {
    const [a, b] = normalizeBetweenValue(row?.value);
    return [toNumberIfNumeric(a, filterType), toNumberIfNumeric(b, filterType)];
  }

  if (LIST_OPS.has(op)) {
    return normalizeListValue(row?.value).map((x) => toNumberIfNumeric(x, filterType));
  }

  const scalar = Array.isArray(row?.value) ? row?.value?.[0] ?? "" : row?.value;
  return toNumberIfNumeric(String(scalar ?? "").trim(), filterType);
}

function isConditionComplete(row) {
  if (!row?.column || !row?.operator) return false;
  if (NULL_OPS.has(String(row.operator).toLowerCase())) return true;
  return rowHasValue(row);
}

function buildRuleJson(rls, columnProfiles) {
  const logicalOp = String(rls?.logicalOp || "and").toLowerCase() === "or" ? "or" : "and";
  const conditions = (rls?.conditions || [])
    .filter(isConditionComplete)
    .map((row) => {
      const condition = {
        op: row.operator,
        column: row.column,
      };
      if (!NULL_OPS.has(row.operator)) {
        condition.value = buildRuleValue(row, columnProfiles);
      }
      return condition;
    });

  return {
    op: logicalOp,
    conditions,
  };
}

function toMaskParams(maskType, maskValue) {
  const mt = String(maskType || "").trim().toLowerCase();
  if (mt !== "fixed") return {};
  const text = String(maskValue ?? "").trim();
  if (!text) return {};
  return { replacement: text };
}

function getCoverageLabel({ rlsEnabled, maskingEnabled }) {
  if (rlsEnabled && maskingEnabled) return "Combined";
  if (maskingEnabled) return "Masking";
  if (rlsEnabled) return "RLS";
  return "Disabled";
}

function normalizeEditableRls(rls) {
  return {
    logicalOp: String(rls?.logicalOp || "and").toLowerCase() === "or" ? "or" : "and",
    conditions: (rls?.conditions || []).map((row) => ({
      column: row?.column || "",
      operator: row?.operator || "",
      value: Array.isArray(row?.value) ? [...row.value] : row?.value ?? "",
    })),
  };
}

function normalizeEditableMasking(masking) {
  return {
    rules: (masking?.rules || []).map((rule) => ({
      column: rule?.column || "",
      maskType: rule?.maskType || "",
      maskValue: rule?.maskValue || "",
    })),
  };
}

function PreviewTable({ result, branch }) {
  if (!result) return null;

  return (
    <div className="mt-3 border border-border rounded-md bg-background">
      <div className="px-3 py-2 border-b border-border text-xs text-muted-foreground">
        Preview: <span className="text-foreground font-medium">{branch === "masking" ? "Masking" : "RLS"}</span>
        {" · "}
        Estimated match count: <span className="text-foreground font-medium">{result.estimatedMatchCount}</span>
      </div>
      {result.rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr>
                {Object.keys(result.rows[0]).map((key) => (
                  <th key={key} className="px-3 py-2 text-left font-medium text-muted-foreground">
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((row, idx) => (
                <tr key={`${branch}_${idx}_preview`} className="border-t border-border">
                  {Object.keys(result.rows[0]).map((key) => (
                    <td key={`${branch}_${idx}_${key}`} className="px-3 py-2 text-foreground">
                      {String(row[key] ?? "-")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-3 py-3 text-xs text-muted-foreground">Preview generated without sample rows.</div>
      )}
    </div>
  );
}

export default function PolicyModal({
  open,
  mode = "create",
  initialPolicy = null,
  users = [],
  groups = [],
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(defaultState());
  const [errors, setErrors] = useState({});
  const [sidebarOffset, setSidebarOffset] = useState(0);
  const [validationState, setValidationState] = useState(createValidationState());
  const [previewConfig, setPreviewConfig] = useState(createPreviewConfig());
  const [previewResult, setPreviewResult] = useState(createPreviewState());

  const [dbOptions, setDbOptions] = useState([]);
  const [tableOptions, setTableOptions] = useState([]);
  const [tableColumnsMeta, setTableColumnsMeta] = useState([]);
  const [metadataLoading, setMetadataLoading] = useState(false);

  const resetBranchValidation = useCallback((branch) => {
    setValidationState((prev) => ({
      ...prev,
      [branch]: { ok: false, message: "" },
    }));
  }, []);

  const resetBranchPreview = useCallback((branch) => {
    setPreviewResult((prev) => ({
      ...prev,
      [branch]: null,
    }));
  }, []);

  useEffect(() => {
    if (!open) return;

    if (initialPolicy) {
      setForm({
        id: initialPolicy.id,
        name: initialPolicy.name || "",
        scope: initialPolicy.scope || "user",
        principalId: initialPolicy.principalId || null,
        databaseId: initialPolicy.databaseId ? String(initialPolicy.databaseId) : "",
        tableName: initialPolicy.tableName || "",
        rlsEnabled: initialPolicy.hasRls ?? (initialPolicy.rlsRules || []).length > 0,
        maskingEnabled: initialPolicy.hasMasking ?? (initialPolicy.maskRules || []).length > 0,
        rls: { logicalOp: initialPolicy.rlsLogicalOp || "and", conditions: initialPolicy.rlsRules || [] },
        masking: { rules: initialPolicy.maskRules || [] },
      });
    } else {
      setForm(defaultState());
    }

    setErrors({});
    setValidationState(createValidationState());
    setPreviewConfig(createPreviewConfig());
    setPreviewResult(createPreviewState());
    setDbOptions([]);
    setTableOptions([]);
    setTableColumnsMeta([]);
  }, [open, initialPolicy]);

  useEffect(() => {
    if (!open) return;
    setValidationState(createValidationState());
    setPreviewResult(createPreviewState());
  }, [open, form.name, form.scope, form.principalId, form.databaseId, form.tableName]);

  useEffect(() => {
    if (!open) return;
    resetBranchValidation("rls");
    resetBranchPreview("rls");
  }, [open, form.rlsEnabled, form.rls.conditions, resetBranchPreview, resetBranchValidation]);

  useEffect(() => {
    if (!open) return;
    resetBranchValidation("masking");
    resetBranchPreview("masking");
  }, [open, form.maskingEnabled, form.masking.rules, resetBranchPreview, resetBranchValidation]);

  useEffect(() => {
    if (!open) return;

    const computeSidebarOffset = () => {
      if (window.matchMedia("(max-width: 768px)").matches) {
        setSidebarOffset(0);
        return;
      }

      const sidebar = document.querySelector(".dashboard .sidebar") || document.querySelector(".sidebar");
      if (!sidebar) {
        setSidebarOffset(0);
        return;
      }

      const rect = sidebar.getBoundingClientRect();
      if (rect.width <= 0 || rect.right <= 0) {
        setSidebarOffset(0);
        return;
      }

      setSidebarOffset(Math.max(0, Math.round(rect.right)));
    };

    computeSidebarOffset();
    const rafId = window.requestAnimationFrame(computeSidebarOffset);
    window.addEventListener("resize", computeSidebarOffset);

    const sidebar = document.querySelector(".sidebar");
    let resizeObserver;
    let mutationObserver;

    if (sidebar && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => computeSidebarOffset());
      resizeObserver.observe(sidebar);
    }

    if (sidebar && typeof MutationObserver !== "undefined") {
      mutationObserver = new MutationObserver(() => computeSidebarOffset());
      mutationObserver.observe(sidebar, {
        attributes: true,
        attributeFilter: ["class", "style"],
      });
    }

    return () => {
      window.removeEventListener("resize", computeSidebarOffset);
      window.cancelAnimationFrame(rafId);
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
    };
  }, [open]);

  useEffect(() => {
    if (!open || !form.principalId) return;

    const loadDbOptions = async () => {
      setMetadataLoading(true);
      try {
        const { data } = await api.get("/security/config/db-options", {
          params: {
            principal_type: form.scope,
            principal_id: Number(form.principalId),
          },
        });
        setDbOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        setDbOptions([]);
        toast.error(err?.response?.data?.error || "Failed to load database options");
      } finally {
        setMetadataLoading(false);
      }
    };

    loadDbOptions();
  }, [open, form.scope, form.principalId]);

  useEffect(() => {
    if (!open || !form.principalId || !form.databaseId) {
      setTableOptions([]);
      setTableColumnsMeta([]);
      return;
    }

    const loadTableOptions = async () => {
      setMetadataLoading(true);
      try {
        const { data } = await api.get("/security/config/table-options", {
          params: {
            principal_type: form.scope,
            principal_id: Number(form.principalId),
            source_db_config_id: Number(form.databaseId),
          },
        });
        setTableOptions(data?.assigned_tables || []);
      } catch (err) {
        setTableOptions([]);
        toast.error(err?.response?.data?.error || "Failed to load table options");
      } finally {
        setMetadataLoading(false);
      }
    };

    loadTableOptions();
  }, [open, form.scope, form.principalId, form.databaseId]);

  useEffect(() => {
    if (!open || !form.principalId || !form.databaseId || !form.tableName) {
      setTableColumnsMeta([]);
      return;
    }

    const loadColumns = async () => {
      setMetadataLoading(true);
      try {
        const { data } = await api.get("/security/config/columns", {
          params: {
            principal_type: form.scope,
            principal_id: Number(form.principalId),
            source_db_config_id: Number(form.databaseId),
            tables: form.tableName,
          },
        });
        const tableMeta = data?.tables_metadata?.[form.tableName] || [];
        setTableColumnsMeta(Array.isArray(tableMeta) ? tableMeta : []);
      } catch (err) {
        setTableColumnsMeta([]);
        toast.error(err?.response?.data?.error || "Failed to load table columns");
      } finally {
        setMetadataLoading(false);
      }
    };

    loadColumns();
  }, [open, form.scope, form.principalId, form.databaseId, form.tableName]);

  const selectedDb = useMemo(
    () => dbOptions.find((db) => String(db.id) === String(form.databaseId)),
    [dbOptions, form.databaseId]
  );

  const tableColumns = useMemo(() => tableColumnsMeta.map((column) => column.name), [tableColumnsMeta]);

  const columnProfiles = useMemo(() => {
    const profileMap = {};
    tableColumnsMeta.forEach((column) => {
      profileMap[column.name] = column;
    });
    return profileMap;
  }, [tableColumnsMeta]);

  const fetchColumnValues = useCallback(
    async ({ columnName, q = "", cursor = 0, limit = 10, all = false, signal }) => {
      if (!form.principalId || !form.databaseId || !form.tableName || !columnName) {
        return { items: [], nextCursor: null, totalEstimate: 0 };
      }

      const { data } = await api.get("/security/config/column-values", {
        signal,
        params: {
          principal_type: form.scope,
          principal_id: Number(form.principalId),
          source_db_config_id: Number(form.databaseId),
          table_name: form.tableName,
          column_name: columnName,
          q,
          cursor,
          limit,
          all: all ? 1 : 0,
        },
      });

      return {
        items: Array.isArray(data?.items) ? data.items.map((item) => String(item)) : [],
        nextCursor: data?.next_cursor ?? null,
        totalEstimate: Number(data?.total_estimate ?? 0),
      };
    },
    [form.scope, form.principalId, form.databaseId, form.tableName]
  );

  const principalLabel = useMemo(() => {
    const source = form.scope === "user" ? users : groups;
    return source.find((principal) => principal.id === form.principalId)?.name || "";
  }, [form.scope, form.principalId, users, groups]);

  const hasPrincipal = !!form.principalId;
  const hasDatabase = !!form.databaseId;
  const hasTable = !!form.tableName;
  const canConfigureRules = hasDatabase && hasTable;
  const hasEnabledBranch = form.rlsEnabled || form.maskingEnabled;

  const databaseDisabled = !hasPrincipal;
  const tableDisabled = !hasPrincipal || !hasDatabase;

  const databaseDisabledReason = "Select scope and principal first";
  const tableDisabledReason = hasPrincipal ? "Select database first" : "Select scope, principal, and database first";
  const ruleSetupDisabledReason = !hasDatabase ? "Select database first" : !hasTable ? "Select table first" : "";

  const validRlsCount = useMemo(() => form.rls.conditions.filter(isConditionComplete).length, [form.rls.conditions]);
  const hasInvalidDateRange = useMemo(
    () => form.rls.conditions.some((row) => hasDateBetweenRangeError(row, columnProfiles)),
    [form.rls.conditions, columnProfiles]
  );
  const validMaskCount = useMemo(
    () => form.masking.rules.filter((rule) => rule.column && rule.maskType).length,
    [form.masking.rules]
  );

  const buildMaskRulesPayload = useCallback(
    () =>
      form.masking.rules
        .filter((rule) => rule.column && rule.maskType)
        .map((rule) => ({
          table_name: form.tableName,
          column_name: rule.column,
          mask_type: rule.maskType,
          mask_params_json: toMaskParams(rule.maskType, rule.maskValue),
          is_active: true,
        })),
    [form.masking.rules, form.tableName]
  );

  const buildCommonPayload = useCallback(
    () => ({
      policy_name: String(form.name || "").trim(),
      source_db_config_id: Number(form.databaseId),
      principal_type: form.scope,
      principal_id: Number(form.principalId),
      table_name: form.tableName,
      is_active: true,
    }),
    [form.name, form.databaseId, form.scope, form.principalId, form.tableName]
  );

  const buildValidationPayload = useCallback(
    (branch) => {
      const common = buildCommonPayload();
      if (branch === "rls") {
        return {
          ...common,
          row_rules: form.rlsEnabled
            ? [
                {
                  table_name: form.tableName,
                  rule_json: buildRuleJson(form.rls, columnProfiles),
                  is_active: true,
                },
              ]
            : [],
        };
      }

      return {
        ...common,
        mask_rules: form.maskingEnabled ? buildMaskRulesPayload() : [],
      };
    },
    [buildCommonPayload, buildMaskRulesPayload, columnProfiles, form.maskingEnabled, form.rls, form.rlsEnabled, form.tableName]
  );

  const buildUpsertRequest = useCallback(() => {
    const request = buildCommonPayload();
    request.row_rules =
      form.rlsEnabled && validRlsCount > 0
        ? [
            {
              table_name: form.tableName,
              rule_json: buildRuleJson(form.rls, columnProfiles),
              is_active: true,
            },
          ]
        : [];
    request.mask_rules = form.maskingEnabled ? buildMaskRulesPayload() : [];
    return request;
  }, [
    buildCommonPayload,
    buildMaskRulesPayload,
    columnProfiles,
    form.maskingEnabled,
    form.rls,
    form.rlsEnabled,
    form.tableName,
    validRlsCount,
  ]);

  const buildPreviewPayload = useCallback(
    (branch) => {
      const branchConfig = previewConfig[branch];
      if (branch === "rls") {
        return {
          policy_type: "rls",
          principal_type: form.scope,
          principal_id: Number(form.principalId),
          source_db_config_id: Number(form.databaseId),
          table_name: form.tableName,
          rule_json: buildRuleJson(form.rls, columnProfiles),
          include_sample_rows: !!branchConfig.includeSampleRows,
          sample_limit: Number(branchConfig.sampleLimit || 10),
          context: {
            jwt_claims: {},
            user_attrs: {},
          },
        };
      }

      return {
        policy_type: "masking",
        principal_type: form.scope,
        principal_id: Number(form.principalId),
        source_db_config_id: Number(form.databaseId),
        table_name: form.tableName,
        mask_rules: buildMaskRulesPayload().map((rule) => ({
          table_name: rule.table_name,
          column_name: rule.column_name,
          mask_type: rule.mask_type,
          mask_params_json: rule.mask_params_json,
        })),
        include_sample_rows: !!branchConfig.includeSampleRows,
        sample_limit: Number(branchConfig.sampleLimit || 10),
      };
    },
    [buildMaskRulesPayload, columnProfiles, form.databaseId, form.principalId, form.rls, form.scope, form.tableName, previewConfig]
  );

  const validateForm = useCallback(
    (branch = null) => {
      const nextErrors = {};
      const policyName = String(form.name || "").trim();

      if (!policyName) nextErrors.name = "Policy name is required";
      if (policyName.length > POLICY_NAME_MAX_LENGTH) {
        nextErrors.name = `Policy name must be ${POLICY_NAME_MAX_LENGTH} characters or fewer`;
      }
      if (!form.scope) nextErrors.scope = "Scope is required";
      if (!form.principalId) nextErrors.principalId = "Principal is required";
      if (!form.databaseId) nextErrors.databaseId = "Database is required";
      if (!form.tableName) nextErrors.tableName = "Table is required";
      if (!hasEnabledBranch) nextErrors.coverage = "Enable at least one policy branch";

      if ((!branch || branch === "rls") && form.rlsEnabled) {
        if (hasInvalidDateRange) {
          nextErrors.rls = "Date range is invalid. Start date must be on or before end date.";
        } else if (validRlsCount === 0) {
          nextErrors.rls = "Add at least one valid RLS condition";
        }
      }

      if ((!branch || branch === "masking") && form.maskingEnabled && validMaskCount === 0) {
        nextErrors.masking = "Add at least one valid masking rule";
      }

      setErrors(nextErrors);
      return {
        ok: Object.keys(nextErrors).length === 0,
        errors: nextErrors,
      };
    },
    [
      form.name,
      form.scope,
      form.principalId,
      form.databaseId,
      form.tableName,
      form.rlsEnabled,
      form.maskingEnabled,
      hasEnabledBranch,
      hasInvalidDateRange,
      validMaskCount,
      validRlsCount,
    ]
  );

  const reviewSummary = useMemo(() => {
    const dbLabel = selectedDb?.display_label || selectedDb?.name || "";
    const ruleCount = (form.rlsEnabled ? validRlsCount : 0) + (form.maskingEnabled ? validMaskCount : 0);
    return {
      principalLabel,
      dbLabel,
      ruleCount,
      coverageLabel: getCoverageLabel({
        rlsEnabled: form.rlsEnabled,
        maskingEnabled: form.maskingEnabled,
      }),
    };
  }, [selectedDb, validRlsCount, validMaskCount, principalLabel, form.rlsEnabled, form.maskingEnabled]);

  const initialBranchState = useMemo(
    () => ({
      rls: {
        enabled: initialPolicy?.hasRls ?? (initialPolicy?.rlsRules || []).length > 0,
        config: normalizeEditableRls({
          logicalOp: initialPolicy?.rlsLogicalOp || "and",
          conditions: initialPolicy?.rlsRules || [],
        }),
      },
      masking: {
        enabled: initialPolicy?.hasMasking ?? (initialPolicy?.maskRules || []).length > 0,
        config: normalizeEditableMasking({
          rules: initialPolicy?.maskRules || [],
        }),
      },
    }),
    [initialPolicy]
  );

  const isBranchDirty = useCallback(
    (branch) => {
      if (mode !== "edit") {
        return branch === "rls" ? form.rlsEnabled : form.maskingEnabled;
      }

      if (branch === "rls") {
        return JSON.stringify({
          enabled: form.rlsEnabled,
          config: normalizeEditableRls(form.rls),
        }) !== JSON.stringify(initialBranchState.rls);
      }

      return JSON.stringify({
        enabled: form.maskingEnabled,
        config: normalizeEditableMasking(form.masking),
      }) !== JSON.stringify(initialBranchState.masking);
    },
    [form.masking, form.maskingEnabled, form.rls, form.rlsEnabled, initialBranchState, mode]
  );

  const isBranchValidated = useCallback(
    (branch) => {
      if (branch === "rls") {
        if (!form.rlsEnabled) return true;
        if (!isBranchDirty("rls")) return true;
        return validationState.rls.ok;
      }
      if (!form.maskingEnabled) return true;
      if (!isBranchDirty("masking")) return true;
      return validationState.masking.ok;
    },
    [form.maskingEnabled, form.rlsEnabled, isBranchDirty, validationState.masking.ok, validationState.rls.ok]
  );

  const runValidation = async (branch) => {
    if (branch === "rls" && !form.rlsEnabled) return false;
    if (branch === "masking" && !form.maskingEnabled) return false;

    const validation = validateForm(branch);
    if (!validation.ok) {
      setValidationState((prev) => ({
        ...prev,
        [branch]: { ok: false, message: "Validation failed. Fix the highlighted fields." },
      }));
      return false;
    }

    try {
      const { data } = await api.post("/security/policies/validate", buildValidationPayload(branch));
      const errorsFromApi = Array.isArray(data?.errors) ? data.errors : [];
      const ok = !!data?.ok;

      setValidationState((prev) => ({
        ...prev,
        [branch]: {
          ok,
          message: ok
            ? `${branch === "masking" ? "Masking" : "RLS"} validation successful.`
            : errorsFromApi[0] || "Validation failed. Please review rule configuration.",
        },
      }));
      return ok;
    } catch (err) {
      setValidationState((prev) => ({
        ...prev,
        [branch]: { ok: false, message: err?.response?.data?.error || "Validation request failed" },
      }));
      return false;
    }
  };

  const handlePreview = async (branch) => {
    if (!isBranchValidated(branch)) return;

    try {
      const { data } = await api.post("/security/config/rule-preview", buildPreviewPayload(branch));

      if (!data?.ok) {
        resetBranchPreview(branch);
        toast.error(data?.error || data?.validation_errors?.[0] || "Rule preview failed");
        return;
      }

      setPreviewResult((prev) => ({
        ...prev,
        [branch]: {
          estimatedMatchCount: data.estimated_match_count || 0,
          rows: data.sample_rows || [],
        },
      }));
    } catch (err) {
      resetBranchPreview(branch);
      toast.error(err?.response?.data?.error || "Failed to preview rule");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const validation = validateForm();
    if (!validation.ok) return;
    if (!isBranchValidated("rls") || !isBranchValidated("masking")) return;

    await onSave?.({
      id: form.id,
      request: buildUpsertRequest(),
    });
  };

  if (!open) return null;

  const policyNameTrimmedLength = String(form.name || "").trim().length;
  const isPolicyNameTooLong = policyNameTrimmedLength > POLICY_NAME_MAX_LENGTH;
  const isSaveDisabled =
    !String(form.name || "").trim() ||
    isPolicyNameTooLong ||
    !hasPrincipal ||
    !hasDatabase ||
    !hasTable ||
    !hasEnabledBranch ||
    !isBranchValidated("rls") ||
    !isBranchValidated("masking");

  const lockedFieldClass =
    "w-full h-10 px-3 rounded-md border border-primary/30 bg-primary/5 text-muted-foreground cursor-not-allowed flex items-center";
  const toggleActiveClass = "border-primary/40 bg-primary/10 text-primary";
  const toggleInactiveClass = "border-border bg-background text-muted-foreground hover:bg-muted/40";

  const renderBranchCard = ({
    branch,
    enabled,
    title,
    icon: Icon,
    builder,
    error,
    validationMessage,
    previewDisabled,
    previewTitle,
  }) => {
    const branchPreview = previewResult[branch];
    const branchConfig = previewConfig[branch];
    const branchDisabled = !canConfigureRules || !enabled;
    const branchDirty = isBranchDirty(branch);
    const branchReady = isBranchValidated(branch);

    return (
      <div className={`rounded-lg border border-border p-4 ${branchDisabled ? "opacity-70" : ""}`} title={ruleSetupDisabledReason}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="inline-flex items-center gap-2 text-sm font-semibold">
            <Icon size={15} />
            {title}
          </div>
          <button
            type="button"
            onClick={() =>
              setForm((prev) => ({
                ...prev,
                [branch === "rls" ? "rlsEnabled" : "maskingEnabled"]: !enabled,
              }))
            }
            className={`h-8 px-3 rounded-md border text-xs font-medium transition-colors ${
              enabled ? toggleActiveClass : toggleInactiveClass
            }`}
          >
            {enabled ? "Enabled" : "Disabled"}
          </button>
        </div>

        <div className={branchDisabled ? "pointer-events-none" : ""}>{builder}</div>
        {error && <p className="mt-2 text-[10px] text-destructive">{error}</p>}

        <div className="mt-3 pt-3 border-t border-border space-y-3">
          <div className={`text-[11px] ${validationState[branch].ok ? "text-emerald-600" : "text-muted-foreground"}`}>
            {enabled
              ? validationMessage ||
                (branchDirty
                  ? `Validate ${title.toLowerCase()} after completing the settings.`
                  : `${title} is unchanged. Validation is only required if you edit or enable/disable it.`)
              : `Enable ${title.toLowerCase()} to configure, validate, and preview it.`}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto_auto] gap-2 items-end">
            <label className="inline-flex items-center gap-2 text-xs text-muted-foreground h-9">
              <input
                type="checkbox"
                checked={branchConfig.includeSampleRows}
                onChange={(e) =>
                  setPreviewConfig((prev) => ({
                    ...prev,
                    [branch]: {
                      ...prev[branch],
                      includeSampleRows: e.target.checked,
                    },
                  }))
                }
                disabled={!enabled}
              />
              Include sample rows
            </label>
            <div className="space-y-1">
              <label className="text-[11px] text-muted-foreground">Sample limit</label>
              <input
                type="number"
                min={1}
                max={20}
                disabled={!enabled || !branchConfig.includeSampleRows}
                value={branchConfig.sampleLimit}
                onChange={(e) =>
                  setPreviewConfig((prev) => ({
                    ...prev,
                    [branch]: {
                      ...prev[branch],
                      sampleLimit: Number(e.target.value || 1),
                    },
                  }))
                }
                className="w-full h-9 px-3 rounded-md border border-border bg-background text-xs disabled:bg-muted/40 disabled:text-muted-foreground"
              />
            </div>
            <button
              type="button"
              onClick={() => runValidation(branch)}
              disabled={!enabled}
              title={!enabled ? `Enable ${title.toLowerCase()} first` : ""}
              className="h-9 px-4 rounded-md border border-primary/40 bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/15 disabled:opacity-50 disabled:cursor-not-allowed disabled:text-muted-foreground disabled:border-border disabled:bg-muted/40"
            >
              Validate
            </button>
            <button
              type="button"
              onClick={() => handlePreview(branch)}
              disabled={previewDisabled}
              title={previewTitle}
              className="h-9 px-4 rounded-md border border-primary/40 bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/15 disabled:opacity-50 disabled:cursor-not-allowed disabled:text-muted-foreground disabled:border-border disabled:bg-muted/40"
            >
              Preview
            </button>
          </div>

          {branchReady ? <PreviewTable branch={branch} result={branchPreview} /> : null}
        </div>
      </div>
    );
  };

  return createPortal(
    <div
      className="fixed top-0 bottom-0 right-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
      style={{
        left: `${sidebarOffset}px`,
        width: `calc(100vw - ${sidebarOffset}px)`,
      }}
    >
      <div className="bg-background rounded-xl shadow-2xl w-full max-w-4xl h-[680px] max-h-[92vh] flex flex-col border border-border">
        <div className="flex justify-between items-center px-6 py-4 border-b border-border shrink-0 bg-muted/10">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              {mode === "edit" ? "Edit Security Policy" : "Create Security Policy"}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">Policy setup</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="flex flex-col flex-1 min-h-0">
          <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4">
            <section className="space-y-2 border border-border rounded-lg p-4 bg-background">
              <div className="text-sm font-semibold">Policy Name</div>
              <div className="space-y-1.5">
                <label className="block text-xs text-muted-foreground">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value.slice(0, POLICY_NAME_MAX_LENGTH) }))}
                  placeholder="Enter policy name"
                  className="w-full h-10 px-3 rounded-md border border-border bg-background text-sm"
                  maxLength={POLICY_NAME_MAX_LENGTH}
                />
                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                  <span>Max {POLICY_NAME_MAX_LENGTH} characters</span>
                  <span className={isPolicyNameTooLong ? "text-destructive" : ""}>
                    {policyNameTrimmedLength}/{POLICY_NAME_MAX_LENGTH}
                  </span>
                </div>
                {errors.name && <p className="text-[10px] text-destructive">{errors.name}</p>}
              </div>
            </section>

            <section className="space-y-2 border border-border rounded-lg p-4 bg-background">
              <div className="text-sm font-semibold">Scope & Principal</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-xs text-muted-foreground">Scope</label>
                  <div className="mt-1.5 inline-flex rounded-lg border border-border bg-muted/30 p-1">
                    {[
                      { value: "user", label: "User" },
                      { value: "group", label: "Group" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            scope: opt.value,
                            principalId: null,
                            databaseId: "",
                            tableName: "",
                            rls: { logicalOp: "and", conditions: [] },
                            masking: { rules: [] },
                          }))
                        }
                        className={`px-4 py-1.5 rounded-md text-xs font-medium border ${
                          form.scope === opt.value ? toggleActiveClass : "text-muted-foreground border-transparent hover:bg-muted/40"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {errors.scope && <p className="text-[10px] text-destructive">{errors.scope}</p>}
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs text-muted-foreground">{form.scope === "user" ? "User" : "Group"}</label>
                  <PrincipalSelector
                    scope={form.scope}
                    users={users}
                    groups={groups}
                    selectedId={form.principalId}
                    onSelect={(id) =>
                      setForm((prev) => ({
                        ...prev,
                        principalId: id,
                        databaseId: "",
                        tableName: "",
                        rls: { logicalOp: "and", conditions: [] },
                        masking: { rules: [] },
                      }))
                    }
                    limit={10}
                  />
                  {errors.principalId && <p className="text-[10px] text-destructive">{errors.principalId}</p>}
                </div>
              </div>
            </section>

            <section className="space-y-2 border border-border rounded-lg p-4 bg-background">
              <div className="text-sm font-semibold">Target Object</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5" title={databaseDisabled ? databaseDisabledReason : ""}>
                  <div className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">Database</label>
                    {databaseDisabled ? (
                      <div className={lockedFieldClass}>{selectedDb?.display_label || selectedDb?.name || "Select database..."}</div>
                    ) : (
                      <InlineDropdown
                        value={form.databaseId}
                        onChange={(nextDatabaseId) =>
                          setForm((prev) => ({
                            ...prev,
                            databaseId: nextDatabaseId,
                            tableName: "",
                            rls: { logicalOp: "and", conditions: [] },
                            masking: { rules: [] },
                          }))
                        }
                        options={[
                          { value: "", label: metadataLoading ? "Loading databases..." : "Select database..." },
                          ...dbOptions.map((db) => ({
                            value: String(db.id),
                            label: db.display_label || db.name,
                          })),
                        ]}
                        buttonClassName="h-10 px-3 text-sm"
                      />
                    )}
                    {errors.databaseId && <p className="text-[10px] text-destructive">{errors.databaseId}</p>}
                  </div>
                </div>

                <div className="space-y-1.5" title={tableDisabled ? tableDisabledReason : ""}>
                  <label className="text-xs text-muted-foreground">Table</label>
                  {tableDisabled ? (
                    <div className={lockedFieldClass}>{form.tableName || "Select table..."}</div>
                  ) : (
                    <InlineDropdown
                      value={form.tableName}
                      onChange={(nextTableName) =>
                        setForm((prev) => ({
                          ...prev,
                          tableName: nextTableName,
                          rls: { logicalOp: "and", conditions: [] },
                          masking: { rules: [] },
                        }))
                      }
                      options={[
                        { value: "", label: metadataLoading ? "Loading tables..." : "Select table..." },
                        ...tableOptions.map((table) => ({ value: table, label: table })),
                      ]}
                      buttonClassName="h-10 px-3 text-sm"
                    />
                  )}
                  {errors.tableName && <p className="text-[10px] text-destructive">{errors.tableName}</p>}
                </div>
              </div>
            </section>

            <section className="space-y-3 border border-border rounded-lg p-4 bg-background">
              <div className="text-sm font-semibold">Rule Configuration</div>
              {errors.coverage && <p className="text-[10px] text-destructive">{errors.coverage}</p>}

              {renderBranchCard({
                branch: "rls",
                enabled: form.rlsEnabled,
                title: "RLS Settings",
                icon: Shield,
                builder: (
                  <RlsBuilder
                    value={form.rls}
                    onChange={(value) => setForm((prev) => ({ ...prev, rls: value }))}
                    tableColumns={tableColumns}
                    columnProfiles={columnProfiles}
                    fetchColumnValues={fetchColumnValues}
                  />
                ),
                error: errors.rls,
                validationMessage: validationState.rls.message,
                previewDisabled:
                  !form.rlsEnabled || !isBranchValidated("rls") || validRlsCount <= 0 || hasInvalidDateRange,
                previewTitle:
                  !form.rlsEnabled
                    ? "Enable RLS first"
                    : validRlsCount <= 0
                    ? "Add at least one valid RLS condition to preview"
                    : hasInvalidDateRange
                    ? "Fix the RLS date range before preview"
                    : !isBranchValidated("rls")
                    ? "Validate RLS before preview"
                    : "",
              })}

              {renderBranchCard({
                branch: "masking",
                enabled: form.maskingEnabled,
                title: "Masking Settings",
                icon: Lock,
                builder: (
                  <MaskingBuilder
                    value={form.masking}
                    onChange={(value) => setForm((prev) => ({ ...prev, masking: value }))}
                    tableColumns={tableColumns}
                    columnProfiles={columnProfiles}
                  />
                ),
                error: errors.masking,
                validationMessage: validationState.masking.message,
                previewDisabled:
                  !form.maskingEnabled || !isBranchValidated("masking") || validMaskCount <= 0,
                previewTitle:
                  !form.maskingEnabled
                    ? "Enable masking first"
                    : validMaskCount <= 0
                    ? "Add at least one valid masking rule to preview"
                    : !isBranchValidated("masking")
                    ? "Validate masking before preview"
                    : "",
              })}
            </section>

            <section className="space-y-2 border border-border rounded-lg p-4 bg-muted/20">
              <div className="text-sm font-semibold">Review</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <div><span className="text-muted-foreground">Policy name:</span> {form.name || "-"}</div>
                <div><span className="text-muted-foreground">Principal:</span> {reviewSummary.principalLabel || "-"}</div>
                <div><span className="text-muted-foreground">Coverage:</span> {reviewSummary.coverageLabel}</div>
                <div><span className="text-muted-foreground">Database:</span> {reviewSummary.dbLabel || "-"}</div>
                <div><span className="text-muted-foreground">Table:</span> {form.tableName || "-"}</div>
                <div><span className="text-muted-foreground">Rule count:</span> {reviewSummary.ruleCount}</div>
              </div>
            </section>
          </div>

          <div className="p-4 border-t border-border bg-muted/10 flex justify-between items-center shrink-0 rounded-b-xl">
            <button
              type="button"
              onClick={() => onClose?.()}
              className="h-9 px-4 rounded-md border border-border text-sm inline-flex items-center gap-1 hover:bg-muted"
            >
              Cancel
            </button>

            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={isSaveDisabled}
                title={isSaveDisabled ? "Validate all enabled policy branches before saving" : ""}
                className="h-9 px-5 rounded-md bg-primary text-primary-foreground text-sm inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Shield size={14} />
                {mode === "edit" ? "Update Policy" : "Save Policy"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.getElementById("modal-root")
  );
}
